import { PhraseType } from "../api/phrasebook";
import { BrandBatchSettings } from "../pages/playground/types";
import { getGroupLabelByPhraseType, getContentGroups } from "./content-groups";

/**
 * Normalize a field name for export:
 * - Remove special characters (keep only letters, numbers, and spaces)
 * - Convert to lowercase
 * - Replace spaces with underscores
 */
export function normalizeFieldName(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters
    .toLowerCase()
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/_+/g, "_") // Replace multiple underscores with single
    .replace(/^_|_$/g, ""); // Remove leading/trailing underscores
}

/**
 * Get the custom label for a phrase type from batch settings.
 * Uses content_groups to resolve labels.
 */
export function getPhraseTypeLabel(
  type: PhraseType | string,
  batchSettings?: BrandBatchSettings
): string {
  return getGroupLabelByPhraseType(batchSettings, type);
}

/**
 * Get phrase type sections with custom labels from batch settings.
 * Uses content_groups to include dynamic groups.
 */
export function getPhraseSections(batchSettings?: BrandBatchSettings) {
  return getContentGroups(batchSettings)
    .filter((g) => g.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((g) => ({
      label: g.label,
      type: g.id as unknown as PhraseType,
      key: g.id,
    }));
}

// Maps generation type strings to PhraseType ids for label lookup
const GENERATION_TYPE_TO_PHRASE_TYPE: Record<string, string> = {
  title: "TITLE",
  regularDescription: "EDITORIAL_DESCRIPTION",
  shortDescription: "SHORT_DESCRIPTION",
  longDescription: "LONG_DESCRIPTION",
};

/**
 * Get the generation label for AI generation panel.
 */
export function getGenerationLabel(
  type: string | false,
  batchSettings?: BrandBatchSettings
): string {
  if (!type) return "Generation";

  const phraseTypeId = GENERATION_TYPE_TO_PHRASE_TYPE[type] || type;
  const label = getGroupLabelByPhraseType(batchSettings, phraseTypeId);
  if (label && label !== "Unknown") return `${label} Generation`;

  return "Generation";
}

/**
 * Get the loading message for AI generation.
 */
export function getGeneratingMessage(
  type: string | false,
  batchSettings?: BrandBatchSettings
): string {
  if (!type) return "Generating...";

  const phraseTypeId = GENERATION_TYPE_TO_PHRASE_TYPE[type] || type;
  const label = getGroupLabelByPhraseType(batchSettings, phraseTypeId);
  if (label && label !== "Unknown") return `Generating ${label.toLowerCase()}...`;

  return "Generating...";
}

/**
 * Get normalized field names for export columns.
 * Returns a dynamic record from content_groups.
 * Groups that are part of an export_rule are replaced by the rule's column.
 */
export function getExportFieldNames(batchSettings?: BrandBatchSettings): Record<string, string> {
  const result: Record<string, string> = {};

  // Collect group IDs consumed by export rules
  const mergedGroupIds = new Set<string>();
  for (const rule of batchSettings?.export_rules || []) {
    for (const gid of rule.sourceGroups) {
      mergedGroupIds.add(gid);
    }
  }

  // Individual group columns (skip those merged by a rule)
  for (const group of getContentGroups(batchSettings)) {
    if (mergedGroupIds.has(group.id)) continue;
    if (group.enabled || group.kind === "title") {
      result[group.id] = normalizeFieldName(group.label);
    }
  }

  // Add export rule columns
  for (const rule of batchSettings?.export_rules || []) {
    if (rule.label && rule.sourceGroups.length > 0) {
      result[`rule:${rule.id}`] = normalizeFieldName(rule.label);
    }
  }

  return result;
}
